import {useState, useMemo, useCallback, useEffect} from "react";
import _ from "lodash";
import {Resource} from "../types/resource";
import {swallowException} from "../utils/appUtils";
import {fetchResource} from "../utils/resourceUtils";

/**
 * Fetches resource by executing async function and updating returned reactive resource state
 * with refresh callback.
 * Async function is being called with given parameters.
 * The dependencies array can be specified explicitly for use-cases, when fetch function arguments
 * don't work as dependencies
 */
export const useFetchResource = <TData, Args extends unknown[]>(
    fetchFn: (...params: Args) => Promise<TData>,
    params: Args,
    dependencies: unknown[] = params,
) => {
    const [counter, setCounter] = useState(0);
    const [resource, setResource] = useState<Resource<Promise<TData>>>({
        state: "loading",
    });

    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        let ignore = false;

        fetchResource({
            fetchFn: () => fetchFn(...params),
            onResourceChange: (newResource) => {
                if (!ignore) {
                    setResource(newResource);
                }
            },
        }).catch(swallowException);

        return () => {
            ignore = true;
        };
    }, [counter, fetchFn, setResource, ...dependencies]);

    const refresh = useCallback(() => {
        setCounter((count) => count + 1);
    }, [setCounter]);

    return {resource, refresh};
};

/**
 * Provides a closure with asynchronous methods, executing provided async function and updating returned reactive resource state
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const useResourceManagement = <TMethods extends Record<string, (...params: any[]) => Promise<any>>>(
    methods: TMethods,
) => {
    const [resource, setResource] = useState<Resource<ReturnType<TMethods[keyof TMethods]>>>({
        state: "uninitialized",
    });

    const reset = useCallback(() => setResource({state: "uninitialized"}), [setResource]);

    const managementMethods = useMemo(() => {
        return _.mapValues(
            methods,
            (v) =>
                async (...params: Parameters<typeof v>) =>
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-explicit-any
                    await fetchResource<any>({
                        fetchFn: () => v(...params),
                        onResourceChange: (newResource) => {
                            setResource(newResource);
                        },
                    }),
        ) as {[K in keyof TMethods]: TMethods[K]};
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setResource]);

    return {
        resource,
        reset,
        ...managementMethods,
    };
};

/**
 * Retrieves value of resource or default value
 * @param resource Resource, containing reactive state of the object
 * @param defaultValue A value being returned if Resource is not in "hasData" state
 * @returns Resource value in "hasData" state, default value otherwise
 */
export const useResourceValueOrDefault = <TData>(resource: Resource<Promise<TData>>, defaultValue: TData): TData =>
    resource.state === "hasData" ? resource.data : defaultValue;

/**
 * Retrieves value of resource or default value
 * The data retrieval is seamless, if data is being reloaded and loadable in isLoading state -
 * previous value is returned.
 * @param resource Resource, containing reactive state of the object
 * @param defaultValue A value being returned if Resource is not in "hasData" state
 * @returns Resource value in "hasData" state, default value otherwise
 */
export const useSeamlessResourceValueOrDefault = <TData>(resource: Resource<Promise<TData>>, defaultValue: TData) => {
    const [value, setValue] = useState<TData>(defaultValue);

    useEffect(() => {
        if (resource.state === "hasData") {
            setValue(resource.data);
        } else if (resource.state === "uninitialized") {
            setValue(defaultValue);
        }
    }, [setValue, resource, defaultValue]);

    return value;
};
